import { Box, Typography, useTheme } from '@mui/material';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

interface SegmentLevelBadgeProps {
    level: number;
    maxLevel?: number;
    title?: string;
}

/**
 * Segment bar level badge component.
 * Shows level as filled segments with level colors.
 */
export default function SegmentLevelBadge({ level, maxLevel, title }: SegmentLevelBadgeProps) {
    const theme = useTheme();
    const levelDefinitions = useSelector((state: AppState) => state.levelDefinition);
    const dynamicMaxLevel = maxLevel || levelDefinitions.length;

    // Get level colors from theme
    const getLevelColor = (levelIndex: number) => {
        return theme.palette.levels?.[levelIndex as 1 | 2 | 3 | 4 | 5] || theme.palette.primary.main;
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} title={title}>
            {/* Level Number Display */}
            <Box
                sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 0.5,
                    mr: 1,
                }}
                title={`${levelDefinitions[level - 1]?.title || `Level ${level}`}: ${levelDefinitions[level - 1]?.description || ''}`}
            >
                <Typography
                    variant="caption"
                    sx={{
                        fontSize: '0.65rem',
                        fontWeight: 500,
                        color: 'white',
                    }}
                >
                    {level}
                </Typography>
            </Box>

            {/* Level Segments */}
            {Array.from({ length: dynamicMaxLevel }, (_, i) => (
                <Box
                    key={i}
                    sx={{
                        width: 8,
                        height: 16,
                        borderRadius: 1,
                        backgroundColor: i < level ? getLevelColor(i + 1) : theme.palette.grey[300],
                        transition: 'all 0.2s ease',
                    }}
                />
            ))}
        </Box>
    );
}
